import { SearchIcon } from '@persona-id/design-tokens/icons';
import { noop } from 'lodash';
import { ChangeEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import Input, { InputGroup } from 'design-system/components/Input';
import { isActive, isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import { InputSizes } from 'design-system/mixins/input';

const SearchBar = styled.div``;

interface StyledInputProps {
  flattenDisplay: boolean;
}

const StyledInput = styled(Input)<StyledInputProps>`
  ${({ theme, flattenDisplay }) =>
    flattenDisplay &&
    css`
      border-width: 0px;

      &${isActive}, &${isFocused}, &${isHovered}&${isNotDisabled}, &${isHovered}&${isFocused} {
        border-color: ${theme.color.border.input};
        box-shadow: none;
      }
    `}
`;

export interface OmniSearchProps {
  className?: string;
  title?: ReactNode;
  handleInput: (value: string) => void;
  id?: string;
  value: string;
  placeholder: string;
  dataTest?: string;
  autoFocus?: boolean;
  useClearIcon?: boolean;
  leftIcon?: ReactNode;
  handleOnFocus?: () => void;
  disabled?: boolean;
  flattenDisplay?: boolean;
  size?: InputSizes;
  maxLength?: number;
}

const SearchInput = ({
  className,
  title = 'Search',
  handleInput,
  handleOnFocus = noop,
  id,
  value,
  placeholder,
  dataTest,
  autoFocus,
  useClearIcon = false,
  disabled = false,
  leftIcon = <SearchIcon />,
  flattenDisplay = false,
  size = 'md',
  maxLength,
}: OmniSearchProps) => {
  // We duplicate state and track it separately here. This allows us to:
  // 1. immediately update the <input/> value
  // 2. do a debounced update of the actual state
  // We expect the callback passed to this input to be a debounced that updates
  // props.value.
  const [innerValue, setInnerValue] = useState<string>(value);
  const handleChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>): void => {
      setInnerValue(target.value);
      handleInput(target.value);
    },
    [handleInput, setInnerValue],
  );

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleClear = useCallback(() => {
    setInnerValue('');
    handleInput('');
    handleOnFocus();
    inputRef.current?.focus();
  }, [handleInput, handleOnFocus, inputRef, setInnerValue]);

  const canClear = useClearIcon && !!innerValue;

  return (
    <SearchBar className={className} data-test={dataTest}>
      {title != null && <FormControlLabel htmlFor={id}>{title}</FormControlLabel>}
      <InputGroup
        leftIcon={leftIcon}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onClear={
          canClear &&
          ((e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClear();
          })
        }
        size={size}
      >
        <StyledInput
          id={id}
          onChange={handleChange}
          placeholder={placeholder}
          value={innerValue}
          autoFocus={autoFocus}
          ref={inputRef}
          onFocus={handleOnFocus}
          disabled={disabled}
          maxLength={maxLength}
          flattenDisplay={flattenDisplay}
        />
      </InputGroup>
    </SearchBar>
  );
};

export default SearchInput;
